<template>
  <div v-if="filterDateRange" class="row">

    <div class="col-stage-data col-12">
      <hr class="hr-dark-color hr-thick" />
      <h1 v-if="!loading" class="snug">
        Path To Health 
        <small class="">
          <i v-if="filterDateRange !== 'yesterday'" class="fal fa-info-circle fa-tooltip"><span class="tip">
            When viewing a date range, each stat shows the daily average, not the total.
          </span></i>
        </small>
      </h1>
      <p class="loading text-center" v-if="loading"><i class="far fa-fw fa-pulse fa-spinner"></i> loading patient stages...</p>
      <list-errors :errors="errors" />

      <hr class="hr-dark-color" />

      <div v-if="!loading && stageStats && stageStats.stages && stageStats.stages.length" class="row row-stages">

        <div class="col-6 col-stage stage-overall col-xs-6 col-sm-4 col-md-4 col-lg-3 col-xl">
          <div class="stage-title">
            <p>
              Overall 
              
            </p>
          </div>
          <div class="stage-top">
            <div class="stage-top-header d-flex justify-content-center align-items-center">
              <div class="pt-count text-center">
                <h3><strong>{{ overallTotalPatientCount | formatNumber }}</strong></h3>
                <p class="stage-stat-label-lg">Patients</p>
              </div>
              <div>
                <i class="fal fa-info-circle fa-tooltip"><span class="tip">
                  Totals across all stages on the Path to Health. Patients are counted in the furthest stage they have reached, so each patient is only counted once in the overall stats. The scheduled vs unscheduled breakdown is based on whether the patient has any scheduled procedures, regardless of the stage of those procedures.
                </span></i>
              </div>
            </div> <!-- /.stage-top-header -->

            <div class="row stage-section-su text-center">
              <div class="col-6">
                <h4>{{ overallTotalScheduledPatientCount | formatNumber }}</h4>
                <p class="stage-stat-label-sm text-center">Scheduled</p>
                <p :title="overallTotalScheduledProcAmount | formatCurrencyFull">{{ overallTotalScheduledProcAmount | formatCurrency }}</p>
              </div>
              <div class="col-6">
                <h4>{{ overallTotalUnscheduledPatientCount | formatNumber }}</h4>
                <p class="stage-stat-label-sm text-center">Unscheduled</p>
                <p :title="overallTotalUnscheduledProcAmount | formatCurrencyFull">{{ overallTotalUnscheduledProcAmount | formatCurrency }}</p>
              </div>
            </div> <!-- /.stage-section-su -->

            <div class="stage-img-container"><img class="stage-img" :src="stageStartImage" alt="All Stages" /></div>
          </div> <!-- /.stage-top -->

          <div class="stage-bot">
            <div v-if="stageStatsWonMissed" class="row stage-section-wm">
              <div class="col-6 text-left">
                <p :title="overallWonAmount | formatCurrencyFull">{{ overallWonAmount | formatCurrency }}</p>
                <p class="stage-stat-label">won</p>
              </div>
              <div class="col-6 text-right">
                <p :title="overallMissedAmount | formatCurrencyFull">{{ overallMissedAmount | formatCurrency }}</p>
                <p class="stage-stat-label">missed</p>
              </div>
            </div> <!-- /.stage-section-wm -->
            <div class="row stage-section-wm-sources">
              <div v-if="stageStatsWonMissed" class="col"><ul class="ul-wm-sources list-unstyled">
                <li :title="overallWonClinicalAmount | formatCurrencyFull">{{ overallWonClinicalAmount | formatCurrency }}</li>
                <li :title="overallWonSupportAmount | formatCurrencyFull">{{ overallWonSupportAmount | formatCurrency }}</li>
                <!-- TODO: add won automation amt -->
              </ul></div>
              <div v-if="stageStatsWonMissed" class="col text-center"><ul class="ul-wm-sources list-unstyled">
                <li><i class="fas fa-tooth fa-tooltip"><span class="tip">
                  Clinical
                </span></i></li>
                <li><i class="fas fa-phone fa-tooltip"><span class="tip">
                  Support
                </span></i></li>
                <!-- <li><i class="fas fa-cogs fa-tooltip"><span class="tip">
                  Automation
                </span></i></li> -->
              </ul></div>
              <div v-if="stageStatsWonMissed" class="col text-right"><ul class="ul-wm-sources list-unstyled">
                <li :title="overallMissedClinicalAmount | formatCurrencyFull">{{ overallMissedClinicalAmount | formatCurrency }}</li>
                <li :title="overallMissedSupportAmount | formatCurrencyFull">{{ overallMissedSupportAmount | formatCurrency }}</li>
                <!-- TODO: add missed automation amt -->
              </ul></div>
            </div> <!-- /.stage-section-wm-sources -->
            <p v-if="stageStatsWonMissed" class="snug text-center"><button @click="goToReEngageList()" class="btn btn-sm btn-light-outline btn-icon-left"><i class="fas fa-search-plus"></i>Re-Engage List</button></p>
          </div> <!-- /.stage-bot -->
        </div> <!-- /.col -->

        <div class="col-6 col-stage col-xs-6 col-sm-4 col-md-4 col-lg-3 col-xl" v-for="stage in stageStats.stages" :key="stage.stageNum" :class="'stage-' + stage.stageNum">
          <div class="stage-title">
            <p>
              {{ stageNames[stage.stageNum] }}
            </p>
          </div>
          <div class="stage-top">
            <div class="stage-top-header d-flex justify-content-center align-items-center">
              <div class="pt-count text-center">
                <h3 class="text-center"><strong>{{ stage.patientCount | formatNumber }}</strong></h3>
                <p class="stage-stat-label-lg text-center">Patients</p>
              </div>
              <div>
                <i class="fal fa-info-circle fa-tooltip"><span class="tip">
                  {{ stageTips[stage.stageNum] }}
                </span></i>
              </div>
            </div> <!-- /.stage-top-header -->


            <div class="row stage-section-su text-center">
              <div class="col-6">
                <h4>{{ (stage.scheduledPatientCount || 0) | formatNumber }}</h4>
                <p class="stage-stat-label-sm text-center">Scheduled</p>
                <p :title="stage.scheduledProcAmount | formatCurrencyFull">{{ stage.scheduledProcAmount | formatCurrency }}</p>
              </div>
              <div class="col-6">
                <h4>{{ (stage.unscheduledPatientCount || 0) | formatNumber }}</h4>
                <p class="stage-stat-label-sm text-center">Unscheduled</p>
                <p :title="stage.unscheduledProcAmount | formatCurrencyFull">{{ stage.unscheduledProcAmount | formatCurrency }}</p>
              </div>
            </div> <!-- /.stage-section-su -->

            <div class="stage-img-container"><img class="stage-img" :src="stageImages[stage.stageNum]" :alt="`Stage ${stage.stageNum}: ${stageNames[stage.stageNum]}`" /></div>
          </div> <!-- /.stage-top -->

          <div class="stage-bot">
            <div v-if="stageStatsWonMissed" class="row stage-section-wm">
              <div class="col-6 text-left">
                <p :title="stage.wonAmount | formatCurrencyFull">{{ stage.wonAmount | formatCurrency }}</p>
                <p class="stage-stat-label">won</p>
              </div>
              <div class="col-6 text-right">
                <p :title="stage.missedAmount | formatCurrencyFull">{{ stage.missedAmount | formatCurrency }}</p>
                <p class="stage-stat-label">missed</p>
              </div>
            </div> <!-- /.stage-section-wm -->
            <div v-if="stageStatsWonMissed" class="row stage-section-wm-sources">
              <div class="col"><ul class="ul-wm-sources list-unstyled">
                <li :title="stage.wonClinicalAmount | formatCurrencyFull">{{ stage.wonClinicalAmount | formatCurrency }}</li>
                <li :title="stage.wonSupportAmount | formatCurrencyFull">{{ stage.wonSupportAmount | formatCurrency }}</li>
                <!-- TODO: add won automation amt -->
              </ul></div>
              <div v-if="stageStatsWonMissed" class="col text-center"><ul class="ul-wm-sources list-unstyled">
                <li><i class="fas fa-tooth fa-tooltip"><span class="tip">
                  Clinical
                </span></i></li>
                <li><i class="fas fa-phone fa-tooltip"><span class="tip">
                  Support
                </span></i></li>
                <!-- <li><i class="fas fa-cogs fa-tooltip"><span class="tip">
                  Automation
                </span></i></li> -->
              </ul></div>
              <div v-if="stageStatsWonMissed" class="col text-right"><ul class="ul-wm-sources list-unstyled">
                <li :title="stage.missedClinicalAmount | formatCurrencyFull">{{ stage.missedClinicalAmount | formatCurrency }}</li>
                <li :title="stage.missedSupportAmount | formatCurrencyFull">{{ stage.missedSupportAmount | formatCurrency }}</li>
                <!-- TODO: add missed automation amt -->
              </ul></div>
            </div> <!-- /.stage-section-wm-sources -->
            <p v-if="stageStatsWonMissed" class="snug text-center"><button @click="goToReEngageList(stage.stageNum)" class="btn-re-engage btn btn-sm btn-light-outline btn-icon-left"><i class="fas fa-search-plus"></i>Re-Engage List</button></p>
          </div> <!-- /.stage-bot -->
        </div> <!-- /.col -->

      </div> <!-- /.row-stages -->

    </div> <!-- /.col-stage-data-->
  </div> <!-- /.row-->
</template>

<script>
import Vue from 'vue'
import {mapGetters} from 'vuex'
import moment from 'moment-timezone'
import ListErrors from '../components/ListErrors.vue'

export default {
  props: ['filterDateRange', 'filterDateRangeTimestamps'],
  components: {
    ListErrors,
  },
  data() {
    return {
      loading: false,
      errors: [],
      stageStats: null,
      stageNames: {
        1: 'Emergency Relief',
        2: 'Understand Each Other Fully',
        3: 'Make a Plan Together',
        4: 'Get Active Disease Under Control',
        5: 'Rebuild for the Long Run',
        6: 'Stay Healthy Together',
      },
      stageImages: {
        1: require('../assets/img/icon-patient-path-1@2x.png'),
        2: require('../assets/img/icon-patient-path-2@2x.png'),
        3: require('../assets/img/icon-patient-path-3@2x.png'),
        4: require('../assets/img/icon-patient-path-4@2x.png'),
        5: require('../assets/img/icon-patient-path-5@2x.png'),
        6: require('../assets/img/icon-patient-path-6@2x.png'),
      },
      stageTips: {
        1: 'Completed emergency (D0140) or recare (D0120) exam within the last 24 months and no comp (D0150) on record.',
        2: 'Completed recare (D0120) or comp (D0150) exam within the last 24 months, but no recare or comp completed within the last 8 months.',
        3: 'Completed recare (D0120) or comp (D0150) within the last 8 months, has treatment-planned advancing or rebuilding procedures, and no completed advancing or rebuilding procedures on record.',
        4: 'Completed recare (D0120) or comp (D0150) within the last 8 months, has treatment-planned advancing procedures, and has completed at least one advancing procedure.',
        5: 'Completed recare (D0120) or comp (D0150) within the last 8 months, has completed advancing procedures, has no treatment-planned advancing procedures, and has treatment-planned rebuilding procedures.',
        6: 'Completed recare (D0120) or comp (D0150) within the last 8 months and has no treatment-planned advancing or rebuilding procedures.',
      },
      stageStartImage: require('../assets/img/icon-patient-path-start@2x.png'),
      // clinical/support won amounts are live; missed amounts and automation are still TODO
      stageStatsWonMissed: true,
    };
  },
  computed: {
    ...mapGetters(['user', 'custom_claims']),
    overallTotalPatientCount() {
      return this.resolveOverallTotal('totalPatientCount', 'patientCount');
    },
    overallTotalScheduledPatientCount() {
      return this.resolveOverallTotal('totalScheduledPatientCount', 'scheduledPatientCount');
    },
    overallTotalUnscheduledPatientCount() {
      return this.resolveOverallTotal('totalUnscheduledPatientCount', 'unscheduledPatientCount');
    },
    overallTotalScheduledProcAmount() {
      return this.resolveOverallTotal('totalScheduledProcAmount', 'scheduledProcAmount');
    },
    overallTotalUnscheduledProcAmount() {
      return this.resolveOverallTotal('totalUnscheduledProcAmount', 'unscheduledProcAmount');
    },
    overallWonClinicalAmount() {
      return this.resolveOverallTotal('totalWonClinicalAmount', 'wonClinicalAmount');
    },
    overallWonSupportAmount() {
      return this.resolveOverallTotal('totalWonSupportAmount', 'wonSupportAmount');
    },
    overallWonAmount() {
      return this.overallWonClinicalAmount + this.overallWonSupportAmount;
    },
    overallMissedClinicalAmount() {
      return this.resolveOverallTotal('totalMissedClinicalAmount', 'missedClinicalAmount');
    },
    overallMissedSupportAmount() {
      return this.resolveOverallTotal('totalMissedSupportAmount', 'missedSupportAmount');
    },
    overallMissedAmount() {
      return this.overallMissedClinicalAmount + this.overallMissedSupportAmount;
    },
  },
  watch: {
    custom_claims(newVal, oldVal) {
      if(newVal !== oldVal) {
        this.loadPatientStages();
      }
    },
    filterDateRangeTimestamps: {
      handler(newVal, oldVal) {
        if(newVal !== oldVal) {
          this.loadPatientStages();
        }
      },
      deep: true,
    },
  },
  methods: {
    resolveOverallTotal(totalField, stageField) {
      const stats = this.stageStats || {};
      const stages = Array.isArray(stats.stages) ? stats.stages : [];
      const reportedTotal = Number(stats[totalField]) || 0;
      const stageSum = stages.reduce((sum, stage) => sum + (Number(stage && stage[stageField]) || 0), 0);

      // Fallback to summed stage columns when overall totals are missing.
      return reportedTotal > 0 ? reportedTotal : stageSum;
    },
    normalizeStageData(stageNum, stageData = {}) {
      const wonClinicalAmount = Number(stageData.wonClinicalAmount) || 0;
      const wonSupportAmount = Number(stageData.wonSupportAmount) || 0;
      const missedClinicalAmount = Number(stageData.missedClinicalAmount) || 0;
      const missedSupportAmount = Number(stageData.missedSupportAmount) || 0;

      return {
        stageNum,
        stageName: stageData.stageName || `Stage ${stageNum}`,
        patientCount: Number(stageData.patientCount) || 0,
        scheduledPatientCount: Number(stageData.scheduledPatientCount) || 0,
        unscheduledPatientCount: Number(stageData.unscheduledPatientCount) || 0,
        scheduledProcAmount: Number(stageData.scheduledProcAmount) || 0,
        unscheduledProcAmount: Number(stageData.unscheduledProcAmount) || 0,
        wonClinicalAmount,
        wonSupportAmount,
        // automation won-tracking is a future feature, always 0 for now
        wonAutomationAmount: 0,
        wonAmount: wonClinicalAmount + wonSupportAmount,
        missedClinicalAmount,
        missedSupportAmount,
        // automation missed-tracking is a future feature, always 0 for now
        missedAutomationAmount: 0,
        missedAmount: missedClinicalAmount + missedSupportAmount,
      };
    },
    buildStageStatsFromSnapshot(data = {}) {
      const stageNums = [1, 2, 3, 4, 5, 6];
      const stages = stageNums.map((stageNum) => this.normalizeStageData(stageNum, data[`stage_${stageNum}`] || {}));

      return {
        totalPatientCount: Number(data.totalPatientCount) || 0,
        totalScheduledPatientCount: Number(data.totalScheduledPatientCount) || 0,
        totalUnscheduledPatientCount: Number(data.totalUnscheduledPatientCount) || 0,
        totalScheduledProcAmount: Number(data.totalScheduledProcAmount) || 0,
        totalUnscheduledProcAmount: Number(data.totalUnscheduledProcAmount) || 0,
        stages,
      };
    },
    buildAverageStageStats(docs = []) {
      const stageNums = [1, 2, 3, 4, 5, 6];
      const docCount = docs.length;

      if(!docCount) {
        return null;
      }

      const totals = {
        totalPatientCount: 0,
        totalScheduledPatientCount: 0,
        totalUnscheduledPatientCount: 0,
        totalScheduledProcAmount: 0,
        totalUnscheduledProcAmount: 0,
      };

      const stages = stageNums.map((stageNum) => ({
        stageNum,
        stageName: null,
        patientCount: 0,
        scheduledPatientCount: 0,
        unscheduledPatientCount: 0,
        scheduledProcAmount: 0,
        unscheduledProcAmount: 0,
        wonClinicalAmount: 0,
        wonSupportAmount: 0,
        wonAutomationAmount: 0,
        wonAmount: 0,
        missedClinicalAmount: 0,
        missedSupportAmount: 0,
        missedAutomationAmount: 0,
        missedAmount: 0,
      }));

      for(const doc of docs) {
        const data = doc.data() || {};
        totals.totalPatientCount += Number(data.totalPatientCount) || 0;
        totals.totalScheduledPatientCount += Number(data.totalScheduledPatientCount) || 0;
        totals.totalUnscheduledPatientCount += Number(data.totalUnscheduledPatientCount) || 0;
        totals.totalScheduledProcAmount += Number(data.totalScheduledProcAmount) || 0;
        totals.totalUnscheduledProcAmount += Number(data.totalUnscheduledProcAmount) || 0;

        for(const stage of stages) {
          const normalizedStage = this.normalizeStageData(stage.stageNum, data[`stage_${stage.stageNum}`] || {});
          stage.stageName = stage.stageName || normalizedStage.stageName;
          stage.patientCount += normalizedStage.patientCount;
          stage.scheduledPatientCount += normalizedStage.scheduledPatientCount;
          stage.unscheduledPatientCount += normalizedStage.unscheduledPatientCount;
          stage.scheduledProcAmount += normalizedStage.scheduledProcAmount;
          stage.unscheduledProcAmount += normalizedStage.unscheduledProcAmount;
          stage.wonClinicalAmount += normalizedStage.wonClinicalAmount;
          stage.wonSupportAmount += normalizedStage.wonSupportAmount;
          stage.wonAmount += normalizedStage.wonAmount;
          stage.missedClinicalAmount += normalizedStage.missedClinicalAmount;
          stage.missedSupportAmount += normalizedStage.missedSupportAmount;
          stage.missedAmount += normalizedStage.missedAmount;
        }
      }

      return {
        totalPatientCount: totals.totalPatientCount / docCount,
        totalScheduledPatientCount: totals.totalScheduledPatientCount / docCount,
        totalUnscheduledPatientCount: totals.totalUnscheduledPatientCount / docCount,
        totalScheduledProcAmount: totals.totalScheduledProcAmount / docCount,
        totalUnscheduledProcAmount: totals.totalUnscheduledProcAmount / docCount,
        stages: stages.map((stage) => ({
          ...stage,
          patientCount: stage.patientCount / docCount,
          scheduledPatientCount: stage.scheduledPatientCount / docCount,
          unscheduledPatientCount: stage.unscheduledPatientCount / docCount,
          scheduledProcAmount: stage.scheduledProcAmount / docCount,
          unscheduledProcAmount: stage.unscheduledProcAmount / docCount,
          wonClinicalAmount: stage.wonClinicalAmount / docCount,
          wonSupportAmount: stage.wonSupportAmount / docCount,
          wonAmount: stage.wonAmount / docCount,
          missedClinicalAmount: stage.missedClinicalAmount / docCount,
          missedSupportAmount: stage.missedSupportAmount / docCount,
          missedAmount: stage.missedAmount / docCount,
        })),
      };
    },
    async loadPatientStages() {
      this.errors = [];

      if(!this.filterDateRange) {
        return;
      }

      if(!this.custom_claims || !this.custom_claims.customerId || !this.custom_claims.authorized_location || !this.user || !this.user.uid) {
        this.errors.push('Missing required user or location data.');
        return;
      }

      this.loading = true;
      this.stageStats = null;

      try {
        const collectionRef = Vue.firestore
          .collection(`customers/${this.custom_claims.customerId}/locations/${this.custom_claims.authorized_location}/stat_path_to_health`);

        if(this.filterDateRange === 'yesterday') {
          const datestamp = moment.tz('America/Chicago').subtract(1, 'days').format('YYYYMMDD');
          const snapshotDoc = await collectionRef.doc(datestamp).get();

          if(!snapshotDoc.exists) {
            this.errors.push('No nightly patient stage snapshot has been generated yet.');
            return;
          }

          this.stageStats = this.buildStageStatsFromSnapshot(snapshotDoc.data() || {});
        } else {
          if(!this.filterDateRangeTimestamps || !this.filterDateRangeTimestamps.start || !this.filterDateRangeTimestamps.end) {
            this.errors.push('Missing required date range data.');
            return;
          }

          const querySnapshot = await collectionRef
            .orderBy('timestamp', 'asc')
            .where('timestamp', '>=', this.filterDateRangeTimestamps.start)
            .where('timestamp', '<=', this.filterDateRangeTimestamps.end)
            .get();

          if(querySnapshot.empty) {
            this.errors.push('No nightly patient stage snapshot has been generated yet.');
            return;
          }

          this.stageStats = this.buildAverageStageStats(querySnapshot.docs);
        }
      } catch(err) {
        console.error('Error loading patient stage snapshot:', err);
        this.errors.push('Failed to load patient stage data.');
      } finally {
        this.loading = false;
      }
    },
    goToReEngageList(stageNum) {
      this.$router.push({
        name: 'patientOutreachUnscheduled',
        query: stageNum ? {stage: String(stageNum)} : {},
      });
    },
  },
  mounted() {
    this.loadPatientStages();
  },
};
</script>

<style lang="less" scoped>
@import '../assets/less/variables.less';

// page container ----------
.col-stage-data {
  .hr-dark-color {
    margin-top: 30px;
  }
  .hr-dark-color.hr-thick {
    margin-top: 0;
  }
}
.row-stages {
  margin: 0 0 30px; // rm grid padding
}
// columns ----------
.col-stage {
  // width: 14.28%; // 7 cols total (overall + 6 stages)
  font-size: 14px; // override base font size
  line-height: 14px;
  margin-bottom: 40px;

  p,h2,h3,h4,h5 {
    margin: 0;
  }
  padding: 0; // rm grid padding

  .stage-title {
    height: 40px;
    padding: 0 20px 10px;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    position: relative;

    p {
      font-style: italic;
      line-height: 18px;
    }
  }
  .stage-top {
    border: 1px solid @dark-color;
    border-right: none; // override double border between cols
    padding: 20px 10px 0;

    .stage-top-header {
      margin-bottom: 10px;
    }
    .fal.fa-tooltip {
      margin-left: 5px;
    }
    .pt-count {
      padding-left: 19px; // space for tooltip icon
    }
  }
  .stage-img-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 10px;
    margin-top: 10px;
    margin-bottom: -58px; // pull up the image to overlap the top and bottom sections
  }
  .stage-img {
    width: 100%;
    max-width: 134px;
    height: auto;
  }
  .stage-bot {
    // padding: 78px 10px 20px; // add extra top padding to account for the image overlap
    padding: 78px 10px 10px;
  }
  .stage-section-su {
    margin: 0 -10px; // revise col padding

    .col-6:first-child {
      padding: 0 5px;
    }
    .col-6:last-child {
      padding: 0 5px;
    }
  }
  .stage-section-wm {
    margin-bottom: 15px;
  }
  .stage-section-wm-sources {
    margin-bottom: 15px;

    .ul-wm-sources {
      margin: 0;

      li {
        margin: 0;
      }
  }
    .ul-wm-sources .fas {
      font-size: 10px;
      line-height: 14px;
    }
  }
  
}
// add right borders back to last cols in each row at smaller screen sizes where cols stack differently
@media (max-width: @screen-xs-min) {
  .col-stage:nth-child(even) {
    .stage-top {
      border-right: 1px solid @dark-color; // add back right border for last col
    }
  }
}
@media (min-width: @screen-xs-min) and (max-width: @screen-md-min) {
  .col-stage:nth-child(3n) {
    .stage-top {
      border-right: 1px solid @dark-color; // add back right border for last col
    }
  }
}
@media (min-width: @screen-md-min) and (max-width: @screen-lg-min) {
  .col-stage:nth-child(4) {
    .stage-top {
      border-right: 1px solid @dark-color; // add back right border for last col
    }
  }
}

// btns ----------
.btn-icon-left .fas {
  margin-right: 5px;
}
.fal.fa-tooltip {
  color: fadeout(@dark-color, 75%);
  cursor: help;
}
// text styles ----------
.stage-stat-label,
.stage-stat-label-sm,
.stage-stat-label-lg {
  font-size: 11px;
  line-height: 14px;
  text-transform: uppercase;
  font-weight: bold;
}
.stage-stat-label-sm {
  font-size: 9px;
}
.stage-stat-label-lg {
  font-size: 12px;
  font-weight: normal;
}
// individual stage styles ----------
.stage-overall {
  .stage-top {
    border-top: 5px solid @dark-color;
  }
  .stage-bot {
    background-color: fadeout(@dark-color, 90%);
  }
}
.stage-1 {
  .stage-top {
    border-top: 5px solid @color-renken-red;
  }
  .stage-bot {
    background-color: fadeout(@color-renken-red, 90%);
  }
}
.stage-2 {
  .stage-top {
    border-top: 5px solid @color-renken-orange;
  }
  .stage-bot {
    background-color: fadeout(@color-renken-orange, 90%);
  }
}
.stage-3 {
  .stage-top {
    border-top: 5px solid @color-renken-yellow;
  }
  .stage-bot {
    background-color: fadeout(@color-renken-yellow, 90%);
  }
}
.stage-4 {
  .stage-top {
    border-top: 5px solid @color-renken-green;
  }
  .stage-bot {
    background-color: fadeout(@color-renken-green, 90%);
  }
}
.stage-5 {
  .stage-top {
    border-top: 5px solid @color-renken-blue;
  }
  .stage-bot {
    background-color: fadeout(@color-renken-blue, 90%);
  }
}
.stage-6 {
  .stage-top {
    border-top: 5px solid @color-renken-magenta;
    border-right: 1px solid @dark-color; // add back right border for last col
  }
  .stage-bot {
    background-color: fadeout(@color-renken-magenta, 90%);
  }
  .stage-img {
    width: 100%;
    max-width: 116px;
    height: auto;

    @media (min-width: @screen-xs-min) {
      max-width: 112px;
    }
    @media (min-width: @screen-sm-min) {
      max-width: 116px;
    }
    @media (min-width: @screen-md-min) {
      max-width: 116px;
    }
    @media (min-width: @screen-lg-min) {
      max-width: 102px;
    }
  }
}

</style>
